import React from "react";
import { ProductDetailsModal } from "../../../../../../interfaces/ProductObject";
import { DineInKitchenRound } from "./dineInDetailBuilder";
import { DineInBoardActions } from "./useDineInBoardActions";
import DineInDetailItemRow from "./DineInDetailItemRow";
import "./styles/DineInRoundGroup.css";

interface DineInRoundGroupProps {
  round: DineInKitchenRound;
  products: ProductDetailsModal[];
  actions: DineInBoardActions;
}

const formatTime = (iso: string) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";

/**
 * One placed round: a header with its time and whatever it still needs, then
 * its lines.
 *
 * The header's right-hand side is the round's whole state in one glance —
 * a count when things are waiting on a decision, a bulk action when they're
 * only waiting on legs, and a flat statement when there's nothing left. Only
 * the middle one is a button, because it's the only one that does anything.
 */
const DineInRoundGroup: React.FC<DineInRoundGroupProps> = ({
  round,
  products,
  actions,
}) => (
  <section className="dine-in-round">
    <header className="dine-in-round__header">
      <span className="dine-in-round__name">
        Round {round.roundNumber}
        <span className="dine-in-round__time">{formatTime(round.orderedAt)}</span>
      </span>

      {round.actionState === "needsAction" && (
        <span className="dine-in-round__tags">
          <span className="dine-in-round__tag dine-in-round__tag--pending">
            {round.pendingCount} NEED ACTION
          </span>
          <button
            type="button"
            className="dine-in-round__tag dine-in-round__tag--action"
            onClick={() => actions.acceptRound(round)}
            disabled={actions.isDeciding}
            aria-busy={actions.isBusy(round.roundId, "acceptRound")}
          >
            {actions.isBusy(round.roundId, "acceptRound") ? (
              <span className="dine-in-btn-spinner" />
            ) : (
              "ACCEPT ALL"
            )}
          </button>
        </span>
      )}
      {round.actionState === "deliverable" && (
        <button
          type="button"
          className="dine-in-round__tag dine-in-round__tag--action"
          onClick={() => actions.deliverRound(round)}
          disabled={actions.isDeciding}
          aria-busy={actions.isBusy(round.roundId, "serveRound")}
        >
          {actions.isBusy(round.roundId, "serveRound") ? (
            <span className="dine-in-btn-spinner" />
          ) : (
            "MARK ALL SERVED"
          )}
        </button>
      )}
      {round.actionState === "done" && (
        <span className="dine-in-round__tag dine-in-round__tag--done">ALL SERVED</span>
      )}
      {/* actionState "unknown" renders nothing on purpose — see
          DineInRoundActionState. Saying nothing beats claiming a round is
          finished when the statuses don't actually say so. */}
    </header>

    <div className="dine-in-round__items">
      {round.items.map((item) => (
        <DineInDetailItemRow
          key={item.key}
          item={item}
          product={products.find((p) => p.productId === item.productId)}
          actions={actions}
        />
      ))}
    </div>
  </section>
);

export default DineInRoundGroup;
